import mapboxgl from "mapbox-gl";

type FC = GeoJSON.FeatureCollection;

/** Ejecuta fn cuando el estilo esté listo */
function withStyleReady(map: mapboxgl.Map, fn: () => void) {
    if (map.isStyleLoaded()) fn();
    else map.once("idle", fn);
}

/** Devuelve un FeatureCollection SOLO con líneas */
function extractLineFC(fc: FC): FC | null {
    if (!fc || !Array.isArray(fc.features)) return null;
    const lines: GeoJSON.Feature[] = [];
    for (const f of fc.features) {
        const g = (f as any)?.geometry as GeoJSON.Geometry | undefined;
        if (!g) continue;
        if (g.type === "LineString" || g.type === "MultiLineString") lines.push(f);
    }
    return lines.length ? { type: "FeatureCollection", features: lines } : null;
}

/** Línea verde-limón delgada, suave y luminosa */
export function upsertRoute(map: mapboxgl.Map, sourceId: string, fc: FC) {
    const lineFC = extractLineFC(fc);
    withStyleReady(map, () => {
        if (!lineFC) return;

        const src = map.getSource("route-line-src") as mapboxgl.GeoJSONSource | undefined;
        if (src) src.setData(lineFC as any);
        else map.addSource("route-line-src", { type: "geojson", data: lineFC });

        if (map.getLayer("route-line")) {
            try { map.removeLayer("route-line"); } catch { }
        }

        map.addLayer({
            id: "route-line",
            type: "line",
            source: "route-line-src",
            layout: { "line-cap": "round", "line-join": "round" },
            paint: {
                // 🎨 Verde limón brillante y suave
                "line-color": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    4, "#eaff7f",   // limón claro
                    10, "#f3ffb3",  // más pálido al acercar
                    16, "#ffffcc"   // casi blanco-amarillo
                ],
                "line-opacity": 0.9,
                "line-width": [
                    "interpolate", ["linear"], ["zoom"],
                    4, 2.0,
                    7, 2.8,
                    10, 3.8,
                    13, 4.6,
                    16, 5.5
                ],
            },
        });
    });
}

/** Puntos verdes (se mantienen como antes) */
export function upsertRouteNodes(
    map: mapboxgl.Map,
    lineSourceId: string,
    pointSourceId: string,
    pointLayerId: string,
    fromFC?: FC
) {
    withStyleReady(map, () => {
        const src = map.getSource("route-line-src") as mapboxgl.GeoJSONSource | undefined;
        const data = fromFC || (src ? (src as any)._data : null);
        if (!data || data.type !== "FeatureCollection") return;
        const fc = data as FC;

        const pts: GeoJSON.Feature[] = [];
        for (const f of fc.features) {
            const g = (f as any).geometry as GeoJSON.Geometry | undefined;
            if (!g) continue;
            if (g.type === "LineString") {
                const coords = g.coordinates as [number, number][];
                for (let i = 0; i < coords.length; i += 8) {
                    pts.push({
                        type: "Feature",
                        geometry: { type: "Point", coordinates: coords[i] },
                        properties: {},
                    });
                }
                pts.push({
                    type: "Feature",
                    geometry: { type: "Point", coordinates: coords[coords.length - 1] },
                    properties: {},
                });
            }
        }

        const pointsFC: FC = { type: "FeatureCollection", features: pts };
        const existing = map.getSource(pointSourceId) as mapboxgl.GeoJSONSource | undefined;
        if (existing) existing.setData(pointsFC as any);
        else map.addSource(pointSourceId, { type: "geojson", data: pointsFC });

        if (map.getLayer(pointLayerId)) {
            try { map.removeLayer(pointLayerId); } catch { }
        }

        map.addLayer({
            id: pointLayerId,
            type: "circle",
            source: pointSourceId,
            paint: {
                "circle-color": "#a3e635",
                "circle-radius": [
                    "interpolate", ["linear"], ["zoom"],
                    5, 5.0,
                    10, 7.0,
                    14, 9.0,
                    16, 10.5,
                ],
                "circle-opacity": 1,
                "circle-stroke-width": 0,
            },
        } as mapboxgl.CircleLayer);
    });
}
/** (opcional) otros puntos simples */
export function upsertPointsLayer(
    map: mapboxgl.Map,
    sourceId: string,
    layerId: string,
    fc: FC,
    opts: { color: string }
) {
    const existingSrc = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (existingSrc) existingSrc.setData(fc as any);
    else map.addSource(sourceId, { type: "geojson", data: fc });

    if (!map.getLayer(layerId)) {
        map.addLayer({
            id: layerId,
            type: "circle",
            source: sourceId,
            paint: {
                "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 4, 10, 6, 14, 8, 16, 10],
                "circle-stroke-width": 0,
                "circle-color": opts.color,
                "circle-opacity": 0.95
            },
            filter: ["==", ["geometry-type"], "Point"],
        });
    } else {
        map.setPaintProperty(layerId, "circle-stroke-width", 0);
    }
}